import { Accordion } from '@europrocurement/flexy-components';
import {
    BillingInfo,
    SET_RECIPIENT_ID_CTX,
    START_UPDATING_RECIPIENT_CTX,
    STOP_UPDATING_RECIPIENT_CTX,
    UPDATE_COLLECTION_CTX,
    useCollectionBillingCtx,
    useCollectionBillingCtxDispatch,
    useLazyDataLoader,
    useBillingRender,
    useBillingActions,
} from '@europrocurement/l2d-modules';
import { useTiersCtx } from '@europrocurement/l2d-modules/providers/TiersProvider';
import { Card, PaletteColor, useTheme } from '@mui/material';
import * as React from 'react';
import { enqueueSnackbar } from 'notistack';

type BillingAddressSectionProps = {
    collectionIds: number[];
};

const BillingAddressSection: React.FunctionComponent<BillingAddressSectionProps> = function (
    props,
) {
    const { collectionIds } = props;
    const { palette } = useTheme();
    const { getTiersById, getMultipleTiersFiltered, isFetch } = useTiersCtx();
    const { canUpdateBillingInfos } = useBillingRender();
    const { handleRecipientChange, handleBillingTypeChange } = useBillingActions();
    const { getCollectionInvoices, getRecipientId, getCollectionById } = useCollectionBillingCtx();
    const collectionBillingDispatch = useCollectionBillingCtxDispatch();
    const { loadTiers } = useLazyDataLoader();
    const recipientId = getRecipientId();

    const collections = collectionIds
        .map(getCollectionById)
        .filter((collection) => collection !== undefined);
    const mainCollection = collections[0] || undefined;

    React.useEffect(() => {
        if (!recipientId && collectionBillingDispatch) {
            collectionBillingDispatch({
                type: SET_RECIPIENT_ID_CTX,
                payload: mainCollection?.clientId,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainCollection?.clientId]);

    React.useEffect(() => {
        if (!isFetch(mainCollection?.prescripteurId)) {
            loadTiers(mainCollection?.prescripteurId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainCollection?.prescripteurId]);

    if (!mainCollection) {
        return null;
    }

    const prescriber = getTiersById(mainCollection.prescripteurId);
    const sousClients = getMultipleTiersFiltered({ prescripteurId: mainCollection.prescripteurId });
    const billingType =
        collections[0]?.venteIndirecte !== undefined ? collections[0].venteIndirecte : true;
    const prescriberId = mainCollection.prescripteurId;

    const canEdit = canUpdateBillingInfos(getCollectionInvoices());

    const clientAddresses = sousClients.map((client) => ({
        clientId: client.id as number,
        label: client.raisonSociale,
        siren: client.siren as string,
        address: client.adresseFacturation?.fullAddress,
    }));

    const onRecipientChange = async (newRecipientId?: number) => {
        const rId =
            newRecipientId ||
            (prescriberId !== mainCollection.clientOpereId
                ? mainCollection.clientOpereId
                : (sousClients[0].id as number));

        const promises = collectionIds.map((id) => handleRecipientChange(id, rId));

        if (collectionBillingDispatch) {
            collectionBillingDispatch({ type: SET_RECIPIENT_ID_CTX, payload: rId });
            collectionBillingDispatch({ type: START_UPDATING_RECIPIENT_CTX });
        }

        try {
            await Promise.all(promises);
            enqueueSnackbar('Le client facturé a bien été modifié.');
        } finally {
            if (collectionBillingDispatch) {
                collectionBillingDispatch({ type: STOP_UPDATING_RECIPIENT_CTX });
            }
        }
    };

    const onBillingTypeChange = async (newBillingType: boolean) => {
        const promises = collectionIds.map((id) => handleBillingTypeChange(id, newBillingType));

        if (collectionBillingDispatch) {
            collections.map((collection) =>
                collectionBillingDispatch({
                    type: UPDATE_COLLECTION_CTX,
                    payload: { ...collection, venteIndirecte: newBillingType },
                }),
            );
        }

        await Promise.all(promises);
        enqueueSnackbar('Le choix du recouvrement a bien été modifié.');
    };

    if (!recipientId) {
        return null;
    }

    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                boxShadow: 'none',
                padding: 0,
                mx: 0,
            }}
        >
            <Accordion
                sx={{
                    boxShadow: 'none',
                }}
                title="Adresse de facturation"
                titlesSx={{
                    fontWeight: '600',
                    boxShadow: 'none',
                }}
                headerSx={{ boxShadow: 'none' }}
                expanded
                expandColor={palette.text.primary as unknown as PaletteColor}
            >
                <BillingInfo
                    prescriberId={prescriberId}
                    recipientId={recipientId}
                    prescriberLabel={prescriber?.raisonSociale || ' - '}
                    prescriberSiren={prescriber?.siren || ' - '}
                    prescriberAddress={prescriber?.adresseFacturation?.fullAddress || ''}
                    onRecipientChange={onRecipientChange}
                    addresses={clientAddresses}
                    billingType={billingType}
                    onBillingTypeChange={onBillingTypeChange}
                    canEdit={canEdit}
                />
            </Accordion>
        </Card>
    );
};

export default BillingAddressSection;
